import type {
  SowCoachingPhase,
  SowExecCoachingFields,
} from '../types';

export const EXEC_COACHING_TIER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'] as const;

export type ExecCoachingTierOption = (typeof EXEC_COACHING_TIER_OPTIONS)[number];
export type ExecCoachingIdentityField = 'clientCompany' | 'leaderName' | 'coachName' | 'epName';

const IDENTITY_PLACEHOLDERS: Record<ExecCoachingIdentityField, string> = {
  clientCompany: 'Client Company Name',
  leaderName: 'Leader Name',
  coachName: 'Coach Name',
  epName: 'EP Full Name',
};

function copyPhases(phases: SowCoachingPhase[]): SowCoachingPhase[] {
  return phases.map((phase) => ({ ...phase }));
}

/** Replace all instances of the previous identity while preserving other edits. */
export function replaceIntroIdentity(
  intro: string,
  previousValue: string,
  nextValue: string,
  placeholder: string,
): string {
  const replacement = nextValue.trim() || placeholder;
  const candidates = [previousValue.trim(), placeholder].filter(
    (candidate, index, all) => candidate && all.indexOf(candidate) === index,
  );

  for (const candidate of candidates) {
    if (intro.includes(candidate)) return intro.split(candidate).join(replacement);
  }
  return intro;
}

export function updateExecCoachingIdentity(
  fields: SowExecCoachingFields,
  key: ExecCoachingIdentityField,
  value: string,
): SowExecCoachingFields {
  return {
    ...fields,
    [key]: value,
    servicesIntro: replaceIntroIdentity(
      fields.servicesIntro,
      fields[key],
      value,
      IDENTITY_PLACEHOLDERS[key],
    ),
  };
}

/** Apply only the fields whose defaults depend on the selected scope. */
export function applyExecCoachingScope(
  fields: SowExecCoachingFields,
  defaults: SowExecCoachingFields,
): SowExecCoachingFields {
  let servicesIntro = defaults.servicesIntro;
  const identityFields: ExecCoachingIdentityField[] = ['clientCompany', 'leaderName', 'coachName', 'epName'];
  for (const key of identityFields) {
    servicesIntro = replaceIntroIdentity(
      servicesIntro,
      defaults[key],
      fields[key],
      IDENTITY_PLACEHOLDERS[key],
    );
  }

  return {
    ...fields,
    scope: defaults.scope,
    tierLabel: defaults.tierLabel,
    serviceName: defaults.serviceName,
    servicesHeading: defaults.servicesHeading,
    servicesIntro,
    showPhaseHours: defaults.showPhaseHours,
    phases: copyPhases(defaults.phases),
    programLines: defaults.programLines.map((line) => ({ ...line })),
    additionalFeesText: defaults.additionalFeesText,
    budgetTimingText: defaults.budgetTimingText,
    extensionText: defaults.extensionText,
  };
}

export function applyExecCoachingTier(
  fields: SowExecCoachingFields,
  tierLabel: string,
  tierPresets?: Record<string, SowCoachingPhase[]>,
): SowExecCoachingFields {
  const preset = tierPresets?.[tierLabel];
  return {
    ...fields,
    tierLabel,
    ...(preset ? { phases: copyPhases(preset) } : {}),
  };
}

export function selectedExecCoachingTier(tierLabel: string): ExecCoachingTierOption | 'Other' {
  const match = /tier\s*([1-4])/i.exec(tierLabel);
  return match ? (`Tier ${match[1]}` as ExecCoachingTierOption) : 'Other';
}
